import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { Button, Input, Card } from '@/components/ui';
import { useEffectiveBusinessId } from '@/hooks';
import { useSupplier, useSupplierLocations } from '../hooks';
import { SupplierLocationCard, SupplierLocationForm } from '../components';
import { formatPhone, formatPhoneInput } from '@/lib/phoneUtils';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY } from '../types';
import type { Currency, CreateSupplierLocationInput, UpdateSupplierLocationInput } from '../types';

export function SupplierDetailPage() {
  const { supplierId } = useParams<{ supplierId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const effectiveBusinessId = useEffectiveBusinessId();

  const { supplier, isLoading, error, refetch, update, isUpdating } = useSupplier({
    supplierId: supplierId || null,
  });

  const {
    locations,
    isLoading: locationsLoading,
    error: locationsError,
    addLocation,
    updateLocationById,
    removeLocation,
    isAdding,
    isUpdating: isUpdatingLocation,
  } = useSupplierLocations({
    supplierId: supplierId || null,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);

  const [editForm, setEditForm] = useState({
    name: '',
    contact_email: '',
    phone: '',
    default_currency: DEFAULT_CURRENCY as Currency,
    website: '',
    notes: '',
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  // Auto-start edit mode if ?edit=true in URL
  useEffect(() => {
    if (supplier && searchParams.get('edit') === 'true' && !isEditing) {
      setEditForm({
        name: supplier.name,
        contact_email: supplier.contactEmail || '',
        phone: supplier.phone || '',
        default_currency: supplier.defaultCurrency || DEFAULT_CURRENCY,
        website: supplier.website || '',
        notes: supplier.notes || '',
      });
      setIsEditing(true);
      setSearchParams({}, { replace: true });
    }
  }, [supplier, searchParams, isEditing, setSearchParams]);

  const handleStartEdit = () => {
    if (supplier) {
      setEditForm({
        name: supplier.name,
        contact_email: supplier.contactEmail || '',
        phone: supplier.phone || '',
        default_currency: supplier.defaultCurrency || DEFAULT_CURRENCY,
        website: supplier.website || '',
        notes: supplier.notes || '',
      });
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      setSaveError('Supplier name is required');
      return;
    }

    setSaveError(null);

    try {
      await update({
        name: editForm.name.trim(),
        contact_email: editForm.contact_email.trim() || null,
        phone: editForm.phone.trim() || null,
        default_currency: editForm.default_currency,
        website: editForm.website.trim() || null,
        notes: editForm.notes.trim() || null,
      });
      setIsEditing(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update supplier';
      setSaveError(message);
    }
  };

  const handlePhoneChange = (value: string) => {
    setEditForm((prev) => ({
      ...prev,
      phone: formatPhoneInput(value),
    }));
  };

  const handleAddLocation = async (data: CreateSupplierLocationInput | UpdateSupplierLocationInput) => {
    await addLocation(data as Omit<CreateSupplierLocationInput, 'supplier_id'>);
    setIsAddingLocation(false);
    refetch(); // Refresh supplier to update location count
  };

  const handleUpdateLocation = async (data: CreateSupplierLocationInput | UpdateSupplierLocationInput) => {
    if (!editingLocationId) return;
    await updateLocationById(editingLocationId, data as UpdateSupplierLocationInput);
    setEditingLocationId(null);
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (!confirm('Are you sure you want to delete this location?')) {
      return;
    }
    await removeLocation(locationId);
    refetch(); // Refresh supplier to update location count
  };

  const editingLocation = editingLocationId
    ? locations.find((l) => l.id === editingLocationId)
    : undefined;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin w-5 h-5 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-secondary-600">Loading supplier...</span>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !supplier) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">
              Supplier not found
            </h2>
            <p className="text-secondary-500 mb-6">
              {error || 'The supplier you are looking for does not exist.'}
            </p>
            <Link to="/suppliers">
              <Button variant="outline">Back to Suppliers</Button>
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/suppliers"
              className="p-2 text-secondary-400 hover:text-secondary-600
                         hover:bg-secondary-100 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-secondary-900">
              {supplier.name}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <Button variant="outline" size="sm" onClick={handleStartEdit}>
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Error */}
        {(saveError || locationsError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{saveError || locationsError}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Supplier Info Card */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-secondary-900 mb-4">
                Company Information
              </h2>

              {isEditing ? (
                <div className="space-y-4">
                  <Input
                    label="Supplier Name"
                    name="name"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    disabled={isUpdating}
                    required
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Contact Email"
                      name="contact_email"
                      type="email"
                      value={editForm.contact_email}
                      onChange={(e) =>
                        setEditForm({ ...editForm, contact_email: e.target.value })
                      }
                      disabled={isUpdating}
                    />
                    <Input
                      label="Phone"
                      name="phone"
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="(416) 555-1234"
                      disabled={isUpdating}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Website"
                      name="website"
                      type="url"
                      value={editForm.website}
                      onChange={(e) =>
                        setEditForm({ ...editForm, website: e.target.value })
                      }
                      placeholder="https://www.supplier.com"
                      disabled={isUpdating}
                    />
                    <div className="w-full">
                      <label htmlFor="defaultCurrency" className="input-label">
                        Default Currency
                      </label>
                      <select
                        id="defaultCurrency"
                        name="defaultCurrency"
                        value={editForm.default_currency}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            default_currency: e.target.value as Currency,
                          })
                        }
                        className="input-field"
                        disabled={isUpdating}
                      >
                        {CURRENCY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="notes" className="input-label">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={editForm.notes}
                      onChange={(e) =>
                        setEditForm({ ...editForm, notes: e.target.value })
                      }
                      placeholder="Internal notes about this supplier..."
                      rows={3}
                      className="input-field resize-none"
                      disabled={isUpdating}
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(false)}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveEdit}
                      isLoading={isUpdating}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-secondary-500">Email</span>
                      <p className="text-secondary-900">
                        {supplier.contactEmail ? (
                          <a
                            href={`mailto:${supplier.contactEmail}`}
                            className="text-primary-600 hover:underline"
                          >
                            {supplier.contactEmail}
                          </a>
                        ) : (
                          <span className="text-secondary-400 italic">Not set</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-secondary-500">Phone</span>
                      <p className="text-secondary-900">
                        {supplier.phone ? (
                          formatPhone(supplier.phone)
                        ) : (
                          <span className="text-secondary-400 italic">Not set</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-secondary-500">Website</span>
                      <p className="text-secondary-900">
                        {supplier.website ? (
                          <a
                            href={supplier.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:underline"
                          >
                            {supplier.website}
                          </a>
                        ) : (
                          <span className="text-secondary-400 italic">Not set</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-secondary-500">Default Currency</span>
                      <p className="text-secondary-900">
                        {supplier.defaultCurrency || 'CAD'}
                      </p>
                    </div>
                  </div>

                  {supplier.notes && (
                    <div>
                      <span className="text-sm text-secondary-500">Notes</span>
                      <p className="text-secondary-900 whitespace-pre-wrap">
                        {supplier.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Locations Card */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-secondary-900">
                  Locations ({supplier.locationCount})
                </h2>
                {!isAddingLocation && !editingLocationId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAddingLocation(true)}
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Location
                  </Button>
                )}
              </div>

              {/* Add Location Form */}
              {isAddingLocation && effectiveBusinessId && (
                <div className="mb-6 p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                  <h3 className="text-sm font-medium text-secondary-700 mb-4">
                    Add New Location
                  </h3>
                  <SupplierLocationForm
                    businessId={effectiveBusinessId}
                    onSubmit={handleAddLocation}
                    onCancel={() => setIsAddingLocation(false)}
                    isSubmitting={isAdding}
                  />
                </div>
              )}

              {/* Edit Location Form */}
              {editingLocationId && editingLocation && effectiveBusinessId && (
                <div className="mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
                  <h3 className="text-sm font-medium text-secondary-700 mb-4">
                    Edit Location
                  </h3>
                  <SupplierLocationForm
                    location={editingLocation}
                    businessId={effectiveBusinessId}
                    onSubmit={handleUpdateLocation}
                    onCancel={() => setEditingLocationId(null)}
                    isSubmitting={isUpdatingLocation}
                  />
                </div>
              )}

              {/* Locations List */}
              {locationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <svg
                    className="animate-spin w-5 h-5 text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              ) : locations.length === 0 ? (
                <div className="text-center py-8">
                  <svg
                    className="w-10 h-10 text-secondary-400 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <p className="text-secondary-500">No locations added yet</p>
                  {!isAddingLocation && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddingLocation(true)}
                      className="mt-3"
                    >
                      Add First Location
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {locations.map((location) => (
                    <SupplierLocationCard
                      key={location.id}
                      location={location}
                      onEdit={
                        editingLocationId
                          ? undefined
                          : () => setEditingLocationId(location.id)
                      }
                      onDelete={() => handleDeleteLocation(location.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
